import type { EventProps } from '@/hooks/analytics/types';
import type { CampaignConfig } from '@/store/campaignConfig/types';

/**
 * Funnel event names for the campaign landing route. One vocabulary for every step, so a
 * dashboard can chain them. `Campaign : CTA Click` is reserved for a future external front
 * door (an InstaPage-style page linking into this route) that would precede
 * `Landing Page : View`. Adding it must not rename anything here.
 */
export const CAMPAIGN_FUNNEL_EVENTS = {
	landingPageView: 'Campaign : Landing Page : View',
	codeValidationSubmitted: 'Campaign : Code Validation : Submitted',
	codeValidationSuccess: 'Campaign : Code Validation : Success',
	codeValidationFailure: 'Campaign : Code Validation : Failure',
	/**
	 * Fired on both completions, told apart by `conversion_type`: 'purchase' from the payment
	 * screen, 'redemption' from the card-less redemption screen.
	 */
	conversion: 'Campaign : Conversion',
	/**
	 * The card-less redemption call was refused or did not answer. Carries the backend's wire code
	 * as `reason` (or `request_failed`), so a spike in refused codes reads differently from an
	 * endpoint outage.
	 */
	redemptionFailure: 'Campaign : Redemption : Failure',
} as const;

/** The campaign fields the funnel events identify a campaign by. */
export type CampaignEventIdentity = Pick<CampaignConfig, 'id' | 'slug' | 'partner'>;

/**
 * The identity every campaign funnel event carries, so any step can be filtered by the same
 * keys. The public campaign view exposes the partner by name only, never by id, so
 * `partner_name` is the partner key here. Server-side purchase events carry the partner UUID
 * as `partner_id`. The two join on `campaign_slug`.
 */
export const campaignEventProps = (campaign: CampaignEventIdentity): EventProps => ({
	campaign_id: campaign.id,
	campaign_slug: campaign.slug,
	partner_name: campaign.partner.name,
});

/**
 * The `subscription.partnership.type` vocabulary. The api's server-side purchase event maps a
 * campaign's reporting category to these same strings, and dashboards filter on them, so treat
 * them as API surface.
 */
export const CAMPAIGN_PARTNERSHIP_TYPES = {
	leadGen: 'dtc_lead_gen',
	brand: 'brand',
} as const;

export type CampaignPartnershipType =
	(typeof CAMPAIGN_PARTNERSHIP_TYPES)[keyof typeof CAMPAIGN_PARTNERSHIP_TYPES];

/**
 * What `promo_code` carries when the campaign takes no code: the literal string "null", not an
 * absent key, so a dashboard can filter on it either way. The api's purchase event uses the
 * same literal.
 */
export const NO_PROMO_CODE = 'null';

/**
 * The public campaign view carries no reporting category, but the api derives `bypass_payment`
 * from it: brand campaigns are gift codes and never take a payment, every other category does.
 * That makes `bypass_payment` an exact stand-in for "brand" here. A future `api` category would
 * report as lead-gen until the public view exposes the category itself.
 */
export const campaignPartnershipType = (
	campaign: Pick<CampaignConfig, 'bypass_payment'>,
): CampaignPartnershipType =>
	campaign.bypass_payment ? CAMPAIGN_PARTNERSHIP_TYPES.brand : CAMPAIGN_PARTNERSHIP_TYPES.leadGen;

export type CampaignPartnershipIdentity = Pick<
	CampaignConfig,
	'slug' | 'partner' | 'bypass_payment' | 'offer_code'
>;

/**
 * The `subscription.partnership` block the partnership dashboard reads. Amplitude renders a
 * nested object with dot paths, so these surface as `subscription.partnership.partner_id`,
 * `.id`, `.promo_code` and `.type`, the same names the api's server-side purchase event uses.
 *
 * `partner_id` is the partner's name: it is how the finance table keys partners, and the only
 * partner identifier the public campaign view exposes. `id` is the campaign slug. `promo_code`
 * is whatever code the passed config holds. At landing, before CampaignCodeEntry has validated
 * the link's code, that is the link's code or NO_PROMO_CODE; at purchase it is the validated
 * code. Product wants the landing event on the code page, so that early snapshot is deliberate.
 * Every campaign-page event carries this block, so one key filters the whole funnel.
 */
export const campaignPartnershipEventProps = (campaign: CampaignPartnershipIdentity): EventProps => ({
	subscription: {
		partnership: {
			partner_id: campaign.partner.name,
			id: campaign.slug,
			promo_code: campaign.offer_code || NO_PROMO_CODE,
			type: campaignPartnershipType(campaign),
		},
	},
});
